import { useEffect, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { ensureBrandFont } from '../../shared/brand-fonts';
import { fontStack, previewRadius, type PreviewBrand } from './brand-form';
import './huisstijl.css';

/** Twee letters uit een naam, voor wie nog geen logo heeft. */
export function initialen(naam: string): string {
  return naam.slice(0, 2).toUpperCase();
}

/**
 * Het voorbeeld rechts op Huisstijl en Merk (I1, uitgebreid in MRK-3): telefoon, zijbalk en
 * eventueel het aanmeldscherm.
 *
 * Inline stijlen en geen tokens, met opzet: dit blok moet het merk tonen zoals het wordt, ook vóór
 * het opslaan. De knop staat in de leesbare tint (zo nodig donkerder), de balk en het icoon in de
 * kleur zelf, de zijbalk in de tint die de server ervan maakt - precies zoals de app het doet. De
 * lettertypen en de afronding staan er ook inline op, zodat een andere keuze meteen zichtbaar is.
 */
export function BrandPreview({
  brand,
  name,
  logoUrl,
  logoDarkUrl,
  appIconUrl,
  loginBackgroundUrl,
  showLogin,
}: {
  brand: PreviewBrand;
  /** De naam in de balk en de zijbalk: de weergavenaam van het bedrijf, of de productnaam. */
  name: string;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  appIconUrl?: string | null;
  loginBackgroundUrl: string | null;
  showLogin?: boolean;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    void ensureBrandFont(brand.fontHeading);
    void ensureBrandFont(brand.fontBody);
  }, [brand.fontHeading, brand.fontBody]);

  const heading: CSSProperties = { fontFamily: fontStack(brand.fontHeading) };
  const body: CSSProperties = { fontFamily: fontStack(brand.fontBody) };
  const card = previewRadius(brand.radius, 14);
  const btnRadius = brand.radius === 'rond' ? 999 : previewRadius(brand.radius, 20);
  const tegel: CSSProperties = {
    background: brand.primary,
    color: brand.onPrimary,
    borderRadius: previewRadius(brand.radius, 10),
  };
  const knop: CSSProperties = {
    background: brand.primaryReadable,
    borderColor: brand.primaryReadable,
    color: '#fff',
    borderRadius: btnRadius,
    ...heading,
  };
  const donkerLogo = logoDarkUrl ?? logoUrl;

  return (
    <div
      className="a-stack"
      data-testid="brand-preview"
      data-font-heading={brand.fontHeading}
      data-font-body={brand.fontBody}
      data-radius={brand.radius}
    >
      <h2 className="a-sect-title">{t('branding.previewPhone')}</h2>
      <div className="a-phone" style={{ width: 280, ...body }} data-testid="theme-preview">
        <div
          className="a-phone-bar"
          style={{ display: 'flex', alignItems: 'center', gap: 8, ...heading }}
          data-testid="phone-preview"
        >
          {logoUrl ? (
            <img src={logoUrl} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          ) : (
            <span className="a-orgtile" style={{ width: 28, height: 28, fontSize: 11, ...tegel }}>
              {initialen(name)}
            </span>
          )}
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        </div>
        <div className="a-phone-body" style={body}>
          <div className="a-phone-card" style={{ borderRadius: card }}>
            <span
              className="a-badge"
              style={{
                alignSelf: 'flex-start',
                background: brand.accent,
                color: brand.onAccent,
                borderRadius: btnRadius,
              }}
            >
              {t('beheer.huisstijl.previewBadge')}
            </span>
            <div className="a-name" style={heading}>
              {t('beheer.huisstijl.previewNoticeTitle')}
            </div>
            <div className="a-meta" style={body}>
              {t('beheer.huisstijl.previewNoticeBody')}
            </div>
          </div>
          <div className="a-phone-card" style={{ borderRadius: card }}>
            <div className="a-name" style={heading}>
              {t('beheer.huisstijl.previewPostTitle')}
            </div>
            <div className="a-meta" style={body}>
              {t('beheer.huisstijl.previewPostMeta')}
            </div>
            {/* Geen echte knop: het is een plaatje, en een knop die niets doet, is voor een
                schermlezer een val. */}
            <span
              className="a-btn block"
              aria-hidden="true"
              style={knop}
              data-testid="preview-button"
            >
              {t('beheer.huisstijl.previewConfirm')}
            </span>
          </div>
          {appIconUrl !== undefined ? (
            <div
              style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}
              data-testid="home-screen-preview"
            >
              {appIconUrl ? (
                <img
                  src={appIconUrl}
                  alt=""
                  style={{ width: 52, height: 52, borderRadius: 14, objectFit: 'cover' }}
                  data-testid="preview-app-icon"
                />
              ) : (
                // Zonder eigen pictogram: het logo op een vlak in de primaire kleur, zoals de
                // server het manifest maakt.
                <span
                  className="a-orgtile"
                  style={{ width: 52, height: 52, ...tegel, borderRadius: 14 }}
                  data-testid="preview-app-icon"
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      style={{ width: 36, height: 36, objectFit: 'contain' }}
                    />
                  ) : (
                    initialen(name)
                  )}
                </span>
              )}
              <span className="a-meta">{t('beheer.huisstijl.previewHome')}</span>
            </div>
          ) : null}
        </div>
      </div>

      <h2 className="a-sect-title">{t('beheer.merk.previewSidebar')}</h2>
      <div
        className="a-brand-sidebar"
        style={{ background: brand.sidebar, borderRadius: card, ...body }}
        data-testid="sidebar-preview"
      >
        <div className="a-brand-sidebar-head" style={heading}>
          {donkerLogo ? (
            <img src={donkerLogo} alt="" data-testid="sidebar-preview-logo" />
          ) : (
            <span className="a-orgtile" style={{ width: 28, height: 28, fontSize: 11, ...tegel }}>
              {initialen(name)}
            </span>
          )}
          <span>{name}</span>
        </div>
        <span
          className="a-brand-sidebar-item on"
          style={{ borderRadius: previewRadius(brand.radius, 10) }}
        >
          {t('beheer.merk.previewNavStart')}
        </span>
        <span
          className="a-brand-sidebar-item"
          style={{ borderRadius: previewRadius(brand.radius, 10) }}
        >
          {t('beheer.merk.previewNavPosts')}
          <span
            className="a-brand-sidebar-count"
            style={{ background: brand.accent, color: brand.onAccent }}
          >
            3
          </span>
        </span>
      </div>

      {showLogin ? (
        <>
          <h2 className="a-sect-title">{t('beheer.merk.previewLogin')}</h2>
          <div
            className="a-brand-login"
            style={{ borderRadius: card, ...body }}
            data-testid="login-preview"
          >
            <div
              className="a-brand-login-side"
              style={{
                background: brand.sidebar,
                backgroundImage: loginBackgroundUrl
                  ? `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url("${loginBackgroundUrl}")`
                  : undefined,
              }}
            >
              <div className="a-brand-sidebar-head" style={heading}>
                {donkerLogo ? (
                  <img src={donkerLogo} alt="" />
                ) : (
                  <span
                    className="a-orgtile"
                    style={{ width: 24, height: 24, fontSize: 10, ...tegel }}
                  >
                    {initialen(name)}
                  </span>
                )}
                <span>{name}</span>
              </div>
              <div
                className="a-brand-login-title"
                style={heading}
                data-testid="login-preview-title"
              >
                {brand.loginTitle ?? t('beheer.merk.previewLoginTitle')}
              </div>
              <div className="a-brand-login-text">
                {brand.loginText ?? t('beheer.merk.previewLoginText')}
              </div>
            </div>
            <div className="a-brand-login-form">
              <span className="a-brand-login-field" style={{ borderRadius: card / 2 + 2 }} />
              <span className="a-brand-login-field" style={{ borderRadius: card / 2 + 2 }} />
              <span className="a-btn sm block" aria-hidden="true" style={knop}>
                {t('beheer.merk.previewLoginButton')}
              </span>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
